import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, FlaskConical, ServerOff } from 'lucide-react'

import { healthQuery, type AiStatus } from '@/api/client'
import { cn } from '@/components/ui/utils'
import { useQueueStore } from '@/stores/queueStore'

/**
 * "AI unavailable: reason" across the top of every page, and the hook that
 * lets AI buttons disable themselves (`useAiAvailability().available`).
 *
 * Source of truth: `GET /api/health` (polled) overridden by the latest
 * `ai.health` SSE event, whichever is newer. A server that cannot be
 * reached at all is its own, stronger banner.
 */

export interface AiAvailability {
  /** True when AI jobs can be started (`ok`, `degraded` and `mock` all count). */
  available: boolean
  mode: AiStatus | 'unknown'
  reason: string | null
  /** True while the backend itself is unreachable. */
  serverDown: boolean
  ttsReady: boolean | null
}

export function useAiAvailability(): AiAvailability {
  const health = useQuery(healthQuery)
  const live = useQueueStore((s) => s.aiHealth)
  const fromHealth = health.data?.ai
  const newer = live && fromHealth?.checked_at && live.checked_at ? Date.parse(live.checked_at) >= Date.parse(fromHealth.checked_at) : Boolean(live)
  const status: AiStatus | 'unknown' = newer && live ? live.status : (fromHealth?.status ?? 'unknown')
  const reason = (newer && live ? live.last_error : fromHealth?.last_error) ?? null
  return {
    available: status === 'ok' || status === 'degraded' || status === 'mock',
    mode: status,
    reason,
    serverDown: health.isError,
    ttsReady: health.data ? health.data.tts.ok : null,
  }
}

export function AiHealthBanner() {
  const { mode, reason, serverDown, ttsReady } = useAiAvailability()

  if (serverDown) {
    return (
      <Banner tone="danger" icon={<ServerOff aria-hidden />}>
        The studio server is not answering. Check that the <code className="font-mono">api</code> container is up; this page keeps retrying.
      </Banner>
    )
  }

  if (mode === 'down') {
    return (
      <Banner tone="warn" icon={<AlertTriangle aria-hidden />}>
        <strong className="font-semibold">AI unavailable</strong>
        {reason ? <>: {reason}</> : null}. Indexing, editing and saved stories still work.{' '}
        <Link to="/settings" search={{ tab: 'ai' }} className="underline underline-offset-4">
          AI settings
        </Link>
      </Banner>
    )
  }

  if (mode === 'degraded' && reason) {
    return (
      <Banner tone="info" icon={<AlertTriangle aria-hidden />}>
        AI is slow or limited right now: {reason}
      </Banner>
    )
  }

  if (mode === 'mock') {
    return (
      <Banner tone="info" icon={<FlaskConical aria-hidden />}>
        <strong className="font-semibold">Mock AI mode</strong> — no credentials needed; annotations and stories are synthetic.
        {ttsReady === false ? ' Voices are still warming up.' : ''}
      </Banner>
    )
  }

  if (ttsReady === false) {
    return (
      <Banner tone="info" icon={<AlertTriangle aria-hidden />}>
        Voices are still warming up (the narration service is downloading its model). Stories play with the browser's voice until then.
      </Banner>
    )
  }

  return null
}

function Banner({ tone, icon, children }: { tone: 'danger' | 'warn' | 'info'; icon: React.ReactNode; children: React.ReactNode }) {
  const cls = { danger: 'bg-danger-soft text-danger', warn: 'bg-warn-soft text-ink', info: 'bg-info-soft text-ink' }[tone]
  return (
    <div role="status" className={cn('flex items-start gap-2 px-4 py-2 text-sm [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0', cls)}>
      {icon}
      <p className="min-w-0 flex-1">{children}</p>
    </div>
  )
}
